"use client";

import "twin.macro";

import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { UpdateAreaValues, useUpdateArea } from "@/api/areas/mutations";
import { Area } from "@/api/areas/types";
import { Button } from "@/components/buttons";
import Error from "@/components/error";
import Field from "@/components/field";
import Input from "@/components/inputs";
import Label from "@/components/label";

import { GatewaysFieldArray, PostsFieldArray } from "./areaFormFields";

interface CreateAreaModalFormProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  area: Area;
}

export const UpdateAreaModale = ({
  open,
  setOpen,
  area,
}: CreateAreaModalFormProps) => {
  const { t } = useTranslation();
  const updateArea = useUpdateArea(area._id);

  const defaultGateways = area?.gateways
    ? [...area.gateways]
    : [
        {
          name: "",
          latitude: "",
          longitude: "",
        },
      ];

  const defaultPosts = area?.posts
    ? [...area.posts]
    : [
        {
          nom: "",
          address: 0,
          latitude: "",
          longitude: "",
          status: "closed",
        },
      ];

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UpdateAreaValues>({
    defaultValues: {
      name: area.name || "",
      gateways: defaultGateways,
      posts: defaultPosts,
    },
  });

  const onSubmit: SubmitHandler<UpdateAreaValues> = async (data) => {
    try {
      await updateArea.mutateAsync({
        name: data.name,
        posts: data.posts.map((post) => ({
          name: post.name,
          address: +post.address,
          latitude: post.latitude,
          longitude: post.longitude,
          status: post.status,
          problem: post.problem || "",
        })),
        gateways: data.gateways.map((gateway) => ({
          name: gateway.name,
          latitude: gateway.latitude || "",
          longitude: gateway.longitude || "",
        })),
      });
      setOpen(false);
      reset({
        name: data.name,
        posts: data.posts.map((post) => ({
          name: post.name,
          address: +post.address,
          latitude: post.latitude,
          longitude: post.longitude,
          status: post.status,
          problem: post.problem || "",
        })),
        gateways: data.gateways.map((gateway) => ({
          name: gateway.name,
          latitude: gateway.latitude || "",
          longitude: gateway.longitude || "",
        })),
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={setOpen} tw="relative z-10">
      <DialogBackdrop tw="fixed inset-0 bg-gray-500/75 transition-opacity duration-500 ease-in-out data-[closed]:opacity-0" />

      <div tw="fixed inset-0 overflow-hidden">
        <div tw="absolute inset-0 overflow-hidden">
          <div tw="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <DialogPanel
              transition
              tw="pointer-events-auto relative w-screen max-w-2xl transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
            >
              <div tw="flex h-full flex-col bg-white py-6 shadow-xl overflow-auto">
                <div tw="px-4 sm:px-6">
                  <div tw="flex items-start justify-between">
                    <DialogTitle tw="text-lg font-semibold text-gray-900">
                      {t("form.updateArea.title")}
                    </DialogTitle>
                    <div tw="ml-3 flex h-7 items-center">
                      <button
                        type="button"
                        onClick={() => setOpen(false)}
                        tw="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600"
                      >
                        <span tw="absolute -inset-2.5" />
                        <span tw="sr-only">{t("form.close")}</span>
                        <XMarkIcon aria-hidden="true" tw="w-6 h-6" />
                      </button>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                  <div tw="flex flex-col mt-8 gap-6 px-4 sm:px-6">
                    <Field tw="flex-1">
                      <Label>{t("form.addArea.name")}</Label>
                      <Input
                        {...register("name")}
                        invalid={Boolean(errors.name)}
                      />
                      {errors.name?.type === "required" && (
                        <Error>{t("form.mandatory")}</Error>
                      )}
                    </Field>

                    <GatewaysFieldArray<UpdateAreaValues>
                      control={control}
                      register={register}
                    />
                    <PostsFieldArray control={control} register={register} />
                  </div>

                  <div tw="flex mt-8 shrink-0 justify-end px-4 py-4 gap-4">
                    <Button white type="button" onClick={() => setOpen(false)}>
                      {t("global.cancel")}
                    </Button>
                    <Button type="submit" green>
                      {t("global.save")}
                    </Button>
                  </div>
                </form>
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
